import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";
const BASE_URL = process.env.REACT_APP_BASE_URL;

// all business documents
export const fetchBusinessDocuments = createAsyncThunk(
  "business_documents/fetchAll",
  async (arg, { rejectWithValue }) => {
    const params =
      arg && typeof arg === "object" && !Array.isArray(arg)
        ? arg
        : { page: Number(arg) || 1 };
    try {
      const response = await axios.get(`${BASE_URL}/business-sector-documents`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
        params,
      });
      return response.data; // Return the whole response including pagination
    } catch (error) {
      const message = error.response?.data?.message || "Failed load data";
      return rejectWithValue(message);
    }
  }
);

// Add business sector document
export const addBusinessSectorDocument = createAsyncThunk(
  "business_documents/add",
  async (formData, { rejectWithValue }) => {
    try {
      const data = new FormData();
      
      // Append all form fields
      if (formData.business_id) data.append("business_id", formData.business_id);
      if (formData.document_type) data.append("document_type", formData.document_type);
      if (formData.document_date) data.append("document_date", formData.document_date);
      if (formData.expiry_date) data.append("expiry_date", formData.expiry_date);
      if (formData.expiry_date_check) data.append("expiry_date_check", formData.expiry_date_check);
      if (formData.auto_renewal !== undefined && formData.auto_renewal !== null) {
        data.append("auto_renewal", formData.auto_renewal);
      }
      if (formData.status !== undefined && formData.status !== null) {
        data.append("status", formData.status);
      }
      if (formData.reminder_enabled !== undefined && formData.reminder_enabled !== null) {
        data.append("reminder_enabled", formData.reminder_enabled);
      }
      if (formData.file) {
        data.append("file", formData.file);
      }

      const response = await axios.post(`${BASE_URL}/business-sector-documents`, data, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to add document";
      return rejectWithValue(message);
    }
  }
);

// Update document status
export const updateDocumentStatus = createAsyncThunk(
  "business_documents/updateStatus",
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("status", status);

      const response = await axios.post(`${BASE_URL}/business-sector-documents/${id}/status`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          Lang: i18n.language,
        },
      });
      return { id, status, data: response.data };
    } catch (error) {
      const message = error.response?.data?.message || "Failed to update document status";
      return rejectWithValue(message);
    }
  }
);

const BusinessSectorDocsSlice = createSlice({
  name: "business_documents",
  initialState: {
    business_documents: [],
    isLoading: false,
    error: null,
    success: null,
    pagination: null,
  },
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBusinessDocuments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBusinessDocuments.fulfilled, (state, action) => {
        state.isLoading = false;
        // Handle response structure: { status: true, message: "...", data: [...] }
        if (action.payload?.data) {
          if (Array.isArray(action.payload.data)) {
            // Direct array response: { data: [...] }
            state.business_documents = action.payload.data;
            state.pagination = null;
          } else if (action.payload.data?.data) {
            // Paginated response: { data: { data: [...], pagination: {...} } }
            state.business_documents = action.payload.data.data;
            state.pagination = action.payload.data.pagination;
          } else {
            state.business_documents = [];
            state.pagination = null;
          }
        } else {
          state.business_documents = [];
          state.pagination = null;
        }
      })
      .addCase(fetchBusinessDocuments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to load data";
      })
      // Add document
      .addCase(addBusinessSectorDocument.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(addBusinessSectorDocument.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "Document added successfully";
      })
      .addCase(addBusinessSectorDocument.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to add document";
      })
      // Update document status
      .addCase(updateDocumentStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateDocumentStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.data?.message || "Status updated successfully";
        // Update the document status in the list
        const documentIndex = state.business_documents.findIndex(
          (doc) => doc.id === action.payload.id
        );
        if (documentIndex !== -1) {
          state.business_documents[documentIndex].status = action.payload.status;
        }
      })
      .addCase(updateDocumentStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to update document status";
      });
  },
});

export const { clearState } = BusinessSectorDocsSlice.actions;
export default BusinessSectorDocsSlice.reducer;
